/* eslint-disable */
import axios from 'axios';
import { async, fn, string } from '@555platform/utility-belt.js';
import {
  apply,
  applySpec,
  call,
  compose,
  cond,
  construct,
  converge,
  curryN,
  head,
  identity,
  isNil,
  juxt,
  length,
  mergeLeft,
  objOf,
  otherwise,
  partial,
  prop,
  propOr,
  then,
  T,
  unapply,
  useWith,
  when,
} from 'ramda';
import { querySelfProvisioningUserInfo } from '../../models';
import config from '../../config';
import getToken from '../../util/getToken';
import {
  handleHttpErrors,
  RequestFailureError,
  UnknownResponseError,
} from '../errors';
import { resolver } from '../util';


const callAsync = curryN(2, async.call);

const handleAuthFailure = otherwise(cond([
  [prop('response'), handleHttpErrors],
  [prop('request'), construct(RequestFailureError)],
  [T, construct(UnknownResponseError)],
]));
const handleAuthSuccess = then(
  compose(when(prop('error'), prop('error')), propOr({}, 'data')),
);

const provision = fn.supply(
  callAsync((token, data) => axios({
    method: 'put',
    url: `${config.authServer}/c/oauth/v1/provision/user`,
    headers: {
      Authorization: `Bearer ${token}`,
      Referer: config.loginSuccess,
    },
    data,
    timeout: 5000,
  })),
  [
    () => getToken('portaltoken'),
    (email, role) => ({ email, role }),
  ],
);

const provisionUser = resolver.authenticatedResolver.createResolver(
  compose(
    handleAuthFailure,
    handleAuthSuccess,
    converge(
      provision,
      [resolver.argProp('email'), resolver.argProp('role')],
    ),
  ),
);

const provisioned = compose(
  querySelfProvisioningUserInfo('findOne'),
  objOf('user_email'),
);

const getProvisionedUser = resolver.compose(
  when(isNil, construct(RequestFailureError)),
  provisioned,
  resolver.argProp('email'),
);

const getProvisionedUsers = resolver.compose(
  apply(
    useWith(
      mergeLeft,
      [applySpec({ users: identity, count: length })],
    ),
  ),
  juxt([
    converge(
      querySelfProvisioningUserInfo('findAllForTeam'),
      [
        resolver.argProp('team'),
        resolver.argProp('skip'),
        resolver.argProp('limit'),
      ],
    ),
    applySpec({
      skip: resolver.argProp('skip'),
      limit: resolver.argProp('limit'),
    }),
  ]),
);

const deleteProvisionedUser = resolver.authenticatedResolver.createResolver(
  async (root, { email }) => {
    const servToken = await getToken('portaltoken');
    return axios({
      method: 'delete',
      url: `${config.authServer}/c/oauth/v1/provision/user/${email}`,
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${servToken}`,
      },
    })
      .then((response) => {
        if (response.data.error === undefined) {
          const returnData = {
            message: response.data.message,
            user_email: email,
          };
          return returnData;
        }
        return response.data.error;
      })
      .catch((error) => {
        if (error.response) {
          return handleHttpErrors(error);
        }
        if (error.request) {
          throw new RequestFailureError();
        } else {
          throw new UnknownResponseError();
        }
      });
  },
);

const provisionDefaultRole = resolver.authenticatedResolver.createResolver(
  async (root, { role, roleType }) => {
    const servToken = await getToken('portaltoken');
    return axios({
      method: 'put',
      url: `${config.authServer}/c/oauth/v1/provision/defaults`,
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${servToken}`,
      },
      data: {
        role,
        role_type: roleType,
      },
    })
      .then((response) => {
        if (response.data.error === undefined) {
          return response.data;
        }
        return response.data.error;
      })
      .catch((error) => {
        if (error.response) {
          return handleHttpErrors(error);
        }
        if (error.request) {
          throw new RequestFailureError();
        } else {
          throw new UnknownResponseError();
        }
      });
  },
);

const getDefaultRoles = resolver.authenticatedResolver.createResolver(
  async () => {
    const servToken = await getToken('portaltoken');
    return axios({
      method: 'get',
      url: `${config.authServer}/c/oauth/v1/provision/defaults`,
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${servToken}`,
      },
    })
      .then((response) => {
        if (response.data.error === undefined) {
          return response.data;
        }
        return response.data.error;
      })
      .catch((error) => {
        if (error.response) {
          return handleHttpErrors(error);
        }
        if (error.request) {
          throw new RequestFailureError();
        } else {
          throw new UnknownResponseError();
        }
      });
  },
);

const deleteDefaultRole = resolver.authenticatedResolver.createResolver(
  async (root, { role, roleType }) => {
    const servToken = await getToken('portaltoken');
    return axios({
      method: 'delete',
      url: `${config.authServer}/c/oauth/v1/provision/defaults`,
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${servToken}`,
      },
      data: {
        role,
        role_type: roleType,
      },
    })
      .then((response) => {
        if (response.data.error === undefined) {
          const returnData = {
            message: response.data.message,
            role,
            roleType,
          };
          return returnData;
        }
        return response.data.error;
      })
      .catch((error) => {
        if (error.response) {
          return handleHttpErrors(error);
        }
        if (error.request) {
          throw new RequestFailureError();
        } else {
          throw new UnknownResponseError();
        }
      });
  },
);

const provisionUserWithTeam = resolver.compose(
  call,
  head,
  juxt([
    compose(
      unapply(partial(provisioned)),
      resolver.argProp('email'),
    ),
    converge(
      provision,
      [
        resolver.argProp('email'),
        converge(
          string.createTemplate('${}:${}'),
          [
            resolver.argProp('teamId'),
            resolver.argProp('role'),
          ],
        ),
      ],
    ),
  ]),
);

export default {
  Query: {
    getProvisionedUser,
    getDefaultRoles,
    getProvisionedUsers,
  },
  Mutation: {
    provisionUser,
    provisionDefaultRole,
    deleteProvisionedUser,
    deleteDefaultRole,
    provisionUserWithTeam,
  },
};
